"use client";

import { cn } from "@/lib/utils";
import { ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";

interface CollapsibleContentProps {
  content: string;
  maxHeight?: number;
  className?: string;
}

export function CollapsibleContent({
  content,
  maxHeight = 200,
  className,
}: CollapsibleContentProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldShowButton, setShouldShowButton] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current) {
      setShouldShowButton(contentRef.current.scrollHeight > maxHeight);
    }
  }, [maxHeight, content]);

  return (
    <div className={cn("relative", className)}>
      <div
        ref={contentRef}
        className={cn(
          "transition-all duration-300 ease-in-out overflow-hidden whitespace-pre-wrap text-muted-foreground",
          !isExpanded && "line-clamp-[8]",
        )}
      >
        {content}
      </div>

      {shouldShowButton && (
        <div
          className={cn(
            "flex items-center justify-center transition-opacity duration-200",
            isExpanded ? "mt-4" : "mt-2",
          )}
        >
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="group inline-flex items-center gap-1 px-3 py-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            <span>{isExpanded ? "Show less" : "Read more"}</span>
            <ChevronDown
              className={cn(
                "w-4 h-4 transition-transform duration-200",
                isExpanded ? "rotate-180" : "group-hover:translate-y-0.5",
              )}
            />
          </button>
        </div>
      )}
    </div>
  );
}
